import { useCallback, useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { CheckCircle2, QrCode } from 'lucide-react';
import Modal from '../common/Modal';
import { PayeeAvatar, PayTrustNote } from './PayWidgets';
import { upiUri, payeeDisplayName, canOpenUpiApps, isIosDevice, IOS_UPI_APPS } from '../../utils/upi';

/**
 * Hands a UPI request to the user's UPI app. The expense is saved as soon
 * as the user picks an app (the save call finishes before we leave the
 * page), then the app opens. Nothing is asked afterwards: a web page cannot
 * read the payment result, so picking an app counts as paying, and the user
 * deletes the expense if it did not go through.
 *
 * - Android: save, then the upi:// link opens the system app chooser.
 * - iPhone / iPad: iOS has no chooser, so the user picks GPay, PhonePe or
 *   Paytm. BHIM is not offered: it rejects payment links to a person's UPI
 *   ID from other apps, and the plain upi:// link might open it.
 * - Computer: shows the payment as a QR to scan with a phone; the expense
 *   is saved when the user says they paid.
 *
 * startPayment({ req, amountText, detailLine, save }) resolves to 'saved'
 * or 'cancelled'. save() must create the expense and reject on failure.
 */
export default function useUpiPayment() {
  const [run, setRun] = useState(null); // { req, amountText, detailLine, save, phase, app }
  const [busy, setBusy] = useState(false);
  const resolverRef = useRef(null);

  const finish = useCallback((result) => {
    setRun(null);
    setBusy(false);
    resolverRef.current?.(result);
    resolverRef.current = null;
  }, []);

  // Save first, then leave for the UPI app (href) when given.
  const saveAndOpen = useCallback(async (current, href) => {
    setBusy(true);
    try {
      await current.save();
    } catch {
      setBusy(false);
      finish('cancelled');
      return;
    }
    if (href) window.location.href = href;
    finish('saved');
  }, [finish]);

  const startPayment = useCallback(({ req, amountText, detailLine, save }) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      const current = { req, amountText, detailLine, save };
      if (!canOpenUpiApps()) {
        setRun({ ...current, phase: 'qr' });
      } else if (isIosDevice()) {
        setRun({ ...current, phase: 'choose' });
      } else {
        setRun({ ...current, phase: 'saving' });
        saveAndOpen(current, upiUri(req));
      }
    });
  }, [saveAndOpen]);

  useEffect(() => () => { resolverRef.current = null; }, []);

  const payDialog = run ? (
    <PayDialog
      run={run}
      busy={busy}
      onCancel={() => !busy && finish('cancelled')}
      onPickApp={(app) => saveAndOpen(run, upiUri(run.req, app.prefix))}
      onShowQr={() => setRun({ ...run, phase: 'qr' })}
      onPaidByQr={() => saveAndOpen(run, null)}
    />
  ) : null;

  return { startPayment, payDialog };
}

function PaymentQr({ req }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    QRCode.toDataURL(upiUri(req), { width: 440, margin: 1, errorCorrectionLevel: 'M' })
      .then((url) => alive && setSrc(url))
      .catch(() => alive && setSrc(null));
    return () => { alive = false; };
  }, [req]);
  return (
    <div className="mx-auto w-[220px] h-[220px] rounded-2xl border border-surface-200 bg-white p-2.5 flex items-center justify-center">
      {src ? <img src={src} alt="UPI payment QR" className="w-full h-full" /> : <QrCode className="w-10 h-10 text-surface-300" />}
    </div>
  );
}

function PayeeHeader({ run }) {
  const name = payeeDisplayName(run.req);
  return (
    <div className="flex flex-col items-center text-center gap-1.5">
      <PayeeAvatar vpa={run.req.vpa} name={run.req.name} size={48} />
      <p className="text-2xl font-bold text-surface-900">{run.amountText}</p>
      <p className="text-sm text-surface-600">
        to <span className="font-semibold text-surface-900">{name}</span>
      </p>
      {name !== run.req.vpa && <p className="text-xs text-surface-400 -mt-1">{run.req.vpa}</p>}
    </div>
  );
}

function PayDialog({ run, busy, onCancel, onPickApp, onShowQr, onPaidByQr }) {
  const { phase } = run;
  const title = phase === 'qr' ? 'Scan to pay' : 'Pay with UPI';

  return (
    <Modal isOpen onClose={onCancel} title={title} size="sm" fullScreenMobile={false} disableEscClose={busy}>
      <div className="space-y-4">
        <PayeeHeader run={run} />
        {run.detailLine && <p className="text-center text-xs text-surface-400 -mt-2">{run.detailLine}</p>}

        {(busy || phase === 'saving') && (
          <p className="text-center text-sm text-surface-500">Saving expense...</p>
        )}

        {phase === 'choose' && !busy && (
          <>
            <div className="flex justify-center gap-4 flex-wrap">
              {IOS_UPI_APPS.map((app) => (
                <button
                  key={app.name}
                  type="button"
                  onClick={() => onPickApp(app)}
                  className="flex flex-col items-center gap-1.5 w-16"
                  aria-label={`Pay with ${app.name}`}
                >
                  <span
                    className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-xl font-extrabold"
                    style={{ backgroundColor: app.color }}
                  >
                    {app.initial}
                  </span>
                  <span className="text-xs font-semibold text-surface-700">{app.name}</span>
                </button>
              ))}
            </div>
            <p className="text-center text-[11px] text-surface-400">
              The expense is saved when you pick an app.
            </p>
            <button type="button" onClick={onShowQr} className="btn-secondary w-full gap-2">
              <QrCode className="w-4 h-4" />
              Show QR to pay from another phone
            </button>
          </>
        )}


        {phase === 'qr' && !busy && (
          <>
            <PaymentQr req={run.req} />
            <p className="text-center text-xs text-surface-500 leading-relaxed">
              Open any UPI app on your phone (Google Pay, PhonePe, Paytm) and scan this QR.
              Once paid, confirm below to save the expense.
            </p>
            <div className="space-y-2">
              <button type="button" onClick={onPaidByQr} className="btn-income w-full gap-2">
                <CheckCircle2 className="w-4 h-4" />
                I have paid, save expense
              </button>
              <button type="button" onClick={onCancel} className="btn-secondary w-full">
                It did not go through
              </button>
            </div>
          </>
        )}

        <PayTrustNote />
      </div>
    </Modal>
  );
}
